import { addDays, getHours, getMinutes, isAfter, isBefore, setHours, setMinutes } from "date-fns";

export interface TimeConstraints {
  earliestAllowed: Date | undefined;
  latestAllowed: Date | undefined;
}

/**
 * Infers the correct date for an ambiguous time.
 *
 * The time picker only shows hours and minutes (e.g., "1:00 AM"), which creates
 * ambiguity for overnight shifts. For example, in a 10 PM - 2 AM shift:
 * - "1:00 AM" could mean 1 AM on the first day (before shift start)
 * - "1:00 AM" could mean 1 AM on the second day (during the shift)
 *
 * This function uses constraints to determine which date the user intended.
 *
 * @param ambiguousTime - Time from picker with potentially wrong date
 * @param originalTime - Original time (provides date context)
 * @param constraints - Constraints to help resolve ambiguity
 * @returns The time with correct date inferred
 */
export function inferDateForAmbiguousTime(
  ambiguousTime: Date,
  originalTime: Date,
  constraints: TimeConstraints
): Date {
  const hours = getHours(ambiguousTime);
  const minutes = getMinutes(ambiguousTime);
  const { earliestAllowed, latestAllowed } = constraints;

  const baseTime = setMinutes(setHours(originalTime, hours), minutes);

  const possibleDates = [baseTime, addDays(baseTime, -1), addDays(baseTime, 1)];

  const validDates = possibleDates.filter((date) => {
    const isTooEarly = earliestAllowed && isBefore(date, earliestAllowed);
    const isTooLate = latestAllowed && isAfter(date, latestAllowed);

    return !isTooEarly && !isTooLate;
  });

  if (validDates.length > 0) {
    validDates.sort((a, b) => {
      const aDiff = Math.abs(a.getTime() - originalTime.getTime());
      const bDiff = Math.abs(b.getTime() - originalTime.getTime());

      return aDiff - bDiff;
    });

    return validDates[0];
  }

  return baseTime;
}
